import React, { useState, useEffect, useCallback } from "react";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
} from "recharts";
import { AlertTriangle, ExternalLink, TrendingUp, TrendingDown, DollarSign, Activity } from "lucide-react";
import axios from "axios";
import heartLoading from "../../../assests/heartLoading.gif";



const PIPELINE_TYPE = "Azure pipeline costs";

const APPROXIMATE_NOTE =
  "This product shares the Azure Resource Group with other products. Cost is estimated by apportioning the total RG spend proportionally by table count. Exact per-product attribution is not available.";


const formatMonthLabel = (isoMonth) => {
  if (!isoMonth) return "";
  const [year, month] = isoMonth.split("-");
  const date = new Date(Number(year), Number(month) - 1, 1);
  const mon = date.toLocaleString("en-US", { month: "short" });
  const yr  = String(year).slice(2);
  return `${mon} ${yr}`;
};


const formatMonthFull = (isoMonth) => {
  if (!isoMonth) return "";
  const [year, month] = isoMonth.split("-");
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleString("en-US", { month: "short", year: "numeric" });
};



const fmt = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(n ?? 0);

const fmtShort = (v) => {
  if (Math.abs(v) >= 1000) return `$${(v / 1000).toFixed(1)}k`;
  return `$${v}`;
};


const KpiCard = ({ label, value, sub, subColor }) => (
  <div className="flex-1 min-w-[0px] border border-gray-200 rounded-[10px] bg-white px-[20px] py-[18px]">
    <p className="text-[11px] font-semibold tracking-widest text-gray-400 uppercase mb-[6px]">
      {label}
    </p>
    <div className="text-[28px] font-bold text-gray-900 leading-none">{value}</div>
    {sub && (
      <p
        className={`text-[13px] mt-[6px] font-medium ${
          subColor === "red"
            ? "text-red-500"
            : subColor === "green"
            ? "text-green-600"
            : "text-gray-500"
        }`}
      >
        {sub}
      </p>
    )}
  </div>
);

// ─────────────────────────────────────────────
// Power BI Link (hidden when url is null)
// ─────────────────────────────────────────────
const PowerBILink = ({ url, label }) => {
  if (!url) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="text-[13px] text-[#1d7285] hover:underline inline-flex items-center gap-[4px] font-medium flex-shrink-0"
    >
      {label}
      <ExternalLink className="w-[13px] h-[13px]" />
    </a>
  );
};


const CostTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const envKeys = ["DEV", "PRE-PROD", "PROD", "OTHER"];
  const envPayload = payload.filter((p) => envKeys.includes(p.dataKey));
  const mom = payload.find((p) => p.dataKey === "momPct");
  const total = envPayload.reduce((sum, p) => sum + (p.value ?? 0), 0);

  return (
    <div className="bg-white border border-gray-200 rounded-[8px] shadow-lg px-[14px] py-[10px] text-[13px]">
      <p className="font-bold text-gray-800 mb-[4px]">{label}</p>
      {envPayload.map((p) => (
        <p key={p.dataKey} style={{ color: p.fill }}>
          {p.name}: {fmt(p.value)}
        </p>
      ))}
      <p className="text-gray-700 font-semibold border-t border-gray-100 mt-[6px] pt-[6px]">
        Total: {fmt(total)}
      </p>
      {mom && (
        <p className={`mt-[4px] ${mom.value >= 0 ? "text-orange-500" : "text-green-600"}`}>
          MoM: {mom.value > 0 ? "+" : ""}{mom.value}%
        </p>
      )}
    </div>
  );
};


const JobsTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const success = payload.find((p) => p.dataKey === "success");
  const failure = payload.find((p) => p.dataKey === "failure");
  const total = (success?.value ?? 0) + (failure?.value ?? 0);
  return (
    <div className="bg-white border border-gray-200 rounded-[8px] shadow-lg px-[14px] py-[10px] text-[13px]">
      <p className="font-bold text-gray-800 mb-[4px]">{label}</p>
      {success && <p className="text-[#10b981]">Success: {success.value}</p>}
      {failure && <p className="text-[#f43f5e]">Failure: {failure.value}</p>}
      <p className="text-gray-700 font-semibold border-t border-gray-100 mt-[6px] pt-[6px]">
        Total: {total}
      </p>
    </div>
  );
};


const FailurePctLabel = ({ x, y, width, value }) => {
  if (!value) return null;
  return (
    <text
      x={x + width / 2}
      y={y + 16}
      fill="white"
      textAnchor="middle"
      fontSize={12}
      fontWeight={600}
    >
      {value}%
    </text>
  );
};


const CostTab = ({ productId }) => {
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [emptyState, setEmptyState] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setEmptyState(null);
    setData(null);

    try {
      if (productId) {
        try {
          const res     = await axios.get(
            `${process.env.REACT_APP_API_URL}/getCostDataForProduct/${productId}`
          );
          const payload = res.data;

          if (payload.message === "Product not found") {
            setEmptyState({ type: "not-found" });
            return;
          }

          if (!payload.costAnalysis && !payload.jobsRunSummary) {
            setEmptyState({ type: "no-data", productName: payload.productName });
            return;
          }

          setData(payload);
        } catch (apiErr) {
          if (apiErr?.response?.status === 404) {
            setEmptyState({ type: "not-found" });
            return;
          }
          console.error("API error:", apiErr?.message);
          setError("Failed to load cost data. Please try again.");
        }
      } else {
        await new Promise((r) => setTimeout(r, 2000));
        setData({});
      }
    } catch (err) {
      console.error("Unexpected error:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Loading ──
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-[12px]">
          <img src={heartLoading} alt="Loading..." style={{ width : 200}} />
          <span className="text-gray-500 text-[16px]">Loading health data...</span>
        </div>
      </div>
    );
  }

  // ── Unexpected error ──
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-[16px]">
        <AlertTriangle className="w-[48px] h-[48px] text-gray-300" />
        <p className="text-gray-500 text-[15px]">{error}</p>
        <button
          onClick={fetchData}
          className="px-[16px] py-[8px] bg-[#bb1ccc] text-white rounded-[8px] text-[13px] hover:bg-blue-800 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  // ── 404 product not found ──
  if (emptyState?.type === "not-found") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-[12px]">
        <AlertTriangle className="w-[56px] h-[56px] text-gray-200" />
        <p className="text-[17px] font-semibold text-gray-500">Product not found</p>
        <p className="text-[13px] text-gray-400">
          We couldn't find cost data for the requested product.
        </p>
      </div>
    );
  }

  // ── 200 but no cost data ──
  if (emptyState?.type === "no-data") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-[12px]">
        <Activity className="w-[56px] h-[56px] text-gray-200" />
        <p className="text-[17px] font-semibold text-gray-500">No Health data available</p>
        {emptyState.productName && (
          <p className="text-[13px] text-gray-400">
            Health data has not been tracked for{" "}
            <span className="font-medium">{emptyState.productName} yet.</span>
          </p>
        )}
      </div>
    );
  }

  if (!data) return null;

  const { costAnalysis, jobsRunSummary, productName } = data;

  const costAnalysisData = costAnalysis
    ? {
        cs:            costAnalysis.summary,
        monthlyCosts:  costAnalysis.monthlyCosts ?? [],
        cdr:           costAnalysis.dateRange,
        isApproximate: costAnalysis.isApproximate,
        costReportUrl: costAnalysis.powerBIReportUrl,
      }
    : null;

  const jobsSummaryData = jobsRunSummary
    ? {
        js:          jobsRunSummary.summary,
        monthlyJobs: jobsRunSummary.monthlyJobs ?? [],
        jdr:         jobsRunSummary.dateRange,
        jobsReportUrl: jobsRunSummary.powerBIReportUrl,
      }
    : null;

  // Transform ISO month keys → display labels for charts
  const costChartData = costAnalysisData
    ? costAnalysisData.monthlyCosts.map((d) => ({
        ...d,
        month: formatMonthLabel(d.month),
        momPct: d.momPct ?? null,
        DEV: d.breakdown?.DEV ?? 0,
        PROD: d.breakdown?.PROD ?? 0,
        "PRE-PROD": d.breakdown?.["PRE-PROD"] ?? 0,
        OTHER: d.breakdown?.OTHER ?? 0,
      }))
    : [];

  const jobsChartData = jobsSummaryData
    ? jobsSummaryData.monthlyJobs.map((d) => ({
        ...d,
        month: formatMonthLabel(d.month),
      }))
    : [];

  return (
    <div className="px-[24px] py-[20px] space-y-[28px] bg-white min-h-full">

      {costAnalysisData && (() => {
        const { cs, monthlyCosts, cdr, isApproximate, costReportUrl } = costAnalysisData;
        const momUp = (cs?.latestMonth?.momChangePct ?? 0) >= 0;

        return (
          <section>
            {/* Header */}
            <div className="mb-[12px]">
              <h2 className="text-[22px] font-bold text-gray-900">
                Cost Analysis — {productName}
              </h2>
              <p className="text-[13px] text-gray-400 mt-[2px]">
                {PIPELINE_TYPE} · {formatMonthFull(cdr?.from)} – {formatMonthFull(cdr?.to)} ·
              </p>
              <span
                className={`inline-block mt-[10px] px-[10px] py-[4px] text-[11px] font-semibold text-white rounded-[4px] tracking-wide ${
                  isApproximate ? "bg-[#0d9488]" : "bg-[#2563eb]"
                }`}
              >
                {isApproximate ? "Approximate Cost" : "Exact Cost"}
              </span>
            </div>

            {/* Warning banner — only for approximate */}
            {isApproximate && (
              <div className="flex gap-[12px] items-start bg-amber-50 border border-amber-200 rounded-[10px] px-[16px] py-[14px] mb-[20px]">
                <AlertTriangle className="w-[18px] h-[18px] text-amber-500 flex-shrink-0 mt-[1px]" />
                <p className="text-[13px] text-gray-700 leading-relaxed">
                  <span className="font-semibold">Approximate cost:{" "}</span>
                  {APPROXIMATE_NOTE}{" "}
                  {costReportUrl && (
                    <a
                      href={costReportUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[#1d7285] hover:underline inline-flex items-center gap-[4px] font-medium"
                    >
                      For detailed run-level logs, view Power BI report
                      <ExternalLink className="w-[12px] h-[12px]" />
                    </a>
                  )}
                </p>
              </div>
            )}

            {!isApproximate && costReportUrl && (
              <div className="mb-[16px]">
                <PowerBILink
                  url={costReportUrl}
                  label="For detailed run-level logs, view Power BI report"
                />
              </div>
            )}

            {/* KPI Cards */}
            <div className="flex gap-[14px] mb-[24px] flex-wrap">
              <KpiCard
                label="Latest Month"
                value={fmt(cs?.latestMonth?.value)}
                sub={
                  <span
                    className={`inline-flex items-center gap-[3px] ${
                      momUp ? "text-red-500" : "text-green-600"
                    }`}
                  >
                    {momUp
                      ? <TrendingUp className="w-[13px] h-[13px]" />
                      : <TrendingDown className="w-[13px] h-[13px]" />
                    }
                    {momUp ? "+" : ""}{cs?.latestMonth?.momChangePct}% MoM
                  </span>
                }
              />
              <KpiCard
                label={`Total (${cs?.total?.monthsTracked ?? 0}M)`}
                value={fmt(cs?.total?.value)}
                sub={`${cs?.total?.monthsTracked ?? 0} months tracked`}
              />
              <KpiCard
                label="Monthly Avg"
                value={fmt(cs?.monthlyAvg?.value)}
                sub="per month"
              />
              <KpiCard
                label="Peak Month"
                value={fmt(cs?.peakMonth?.value)}
                sub={formatMonthLabel(cs?.peakMonth?.month)}
              />
            </div>

            {/* Monthly Cost & MoM combo chart */}
            <div className="border border-gray-200 rounded-[12px] bg-white px-[24px] pt-[20px] pb-[10px]">
              <p className="text-[16px] font-bold text-gray-900">Monthly Cost &amp; MoM Change</p>
              <p className="text-[12px] text-gray-400 mt-[2px] mb-[16px]">
                Cost bars (left axis) with MoM % overlay line (right axis)
              </p>
              <div className="flex items-center gap-[20px] mb-[12px] text-[12px] text-gray-500">
                {[["#818cf8","DEV"], ["#a78bfa","PRE-PROD"], ["#4f46e5","PROD"], ["#312e81","OTHER"]].map(([color, label]) => (
                  <span key={label} className="flex items-center gap-[6px]">
                    <span className="inline-block w-[12px] h-[12px] rounded-[3px]" style={{ backgroundColor: color }} />
                    {label}
                  </span>
                ))}
                <span className="flex items-center gap-[6px]">
                  <span className="inline-block w-[20px] h-[2.5px] bg-[#f97316] rounded-full" />
                  MoM %
                </span>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart
                  data={costChartData}
                  margin={{ top: 10, right: 50, left: 10, bottom: 0 }}
                >
                  <CartesianGrid vertical={false} stroke="#f0f0f0" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 12, fill: "#9ca3af" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    yAxisId="cost"
                    orientation="left"
                    tickFormatter={fmtShort}
                    tick={{ fontSize: 12, fill: "#9ca3af" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    yAxisId="mom"
                    orientation="right"
                    tickFormatter={(v) => `${v}%`}
                    tick={{ fontSize: 12, fill: "#9ca3af" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip content={<CostTooltip />} />
                  <Bar yAxisId="cost" dataKey="DEV"      stackId="cost" fill="#818cf8" radius={[0,0,4,4]} barSize={48} name="DEV" />
                  <Bar yAxisId="cost" dataKey="PRE-PROD" stackId="cost" fill="#a78bfa" barSize={48} name="PRE-PROD" />
                  <Bar yAxisId="cost" dataKey="PROD"     stackId="cost" fill="#4f46e5" barSize={48} name="PROD" />
                  <Bar yAxisId="cost" dataKey="OTHER"    stackId="cost" fill="#312e81" radius={[4,4,0,0]} barSize={48} name="OTHER" />
                  <Line
                    yAxisId="mom"
                    type="monotone"
                    dataKey="momPct"
                    stroke="#f97316"
                    strokeWidth={2.5}
                    dot={{ fill: "#f97316", r: 5, strokeWidth: 0 }}
                    activeDot={{ r: 7 }}
                    connectNulls={true}
                    name="MoM %"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </section>
        );
      })()}
      {jobsSummaryData && (() => {
        const { js, monthlyJobs, jdr, jobsReportUrl } = jobsSummaryData;

        return (
          <section>
            {/* Header */}
            <div className="flex items-center justify-between mb-[12px] flex-wrap gap-[8px]">
              <div>
                <h2 className="text-[22px] font-bold text-gray-900">
                  Jobs Run Summary — {productName}
                </h2>
                <p className="text-[13px] text-gray-400 mt-[2px]">
                  Job success &amp; failure counts with DBU consumption &nbsp;–&nbsp;
                  {formatMonthFull(jdr?.from)} – {formatMonthFull(jdr?.to)} ·
                </p>
              </div>
              <PowerBILink
                url={jobsReportUrl}
                label="For detailed run-level logs, view Power BI report"
              />
            </div>

            {/* KPI Cards */}
            <div className="flex gap-[14px] mb-[24px] flex-wrap border border-gray-100 rounded-[12px] bg-gray-50 p-[16px]">
              <KpiCard
                label="Total Jobs"
                value={<span className="text-gray-900">{js?.totalJobs?.value ?? 0}</span>}
                sub={`${js?.totalJobs?.monthsTracked ?? 0} months`}
              />
              <KpiCard
                label="Successful"
                value={<span className="text-[#10b981]">{js?.successful?.value ?? 0}</span>}
                sub={`${js?.successful?.successRatePct ?? 0}% success rate`}
                subColor="green"
              />
              <KpiCard
                label="Failed"
                value={<span className="text-[#f43f5e]">{js?.failed?.value ?? 0}</span>}
                sub={`${js?.failed?.failureRatePct ?? 0}% failure rate`}
                subColor="red"
              />
              <KpiCard
                label="Total DBU Used"
                value={js?.totalDBU?.value ?? 0}
                sub={`${js?.totalDBU?.wastedOnFailures ?? 0} wasted on failures`}
              />
            </div>

            {/* Stacked bar chart */}
            <div className="border border-gray-200 rounded-[12px] bg-white px-[24px] pt-[20px] pb-[10px]">
              <div className="flex items-center justify-between mb-[4px]">
                <p className="text-[16px] font-bold text-gray-900">
                  Job Runs by Month — Success vs Failure
                </p>
                <div className="flex items-center gap-[14px] text-[12px] text-gray-500">
                  <span className="flex items-center gap-[6px]">
                    <span className="inline-block w-[10px] h-[10px] rounded-full bg-[#10b981]" />
                    Success
                  </span>
                  <span className="flex items-center gap-[6px]">
                    <span className="inline-block w-[10px] h-[10px] rounded-full bg-[#f43f5e]" />
                    Failure
                  </span>
                </div>
              </div>
              <p className="text-[12px] text-gray-400 mb-[20px]">
                Stacked count of job executions · green = successful&nbsp; red = failed
              </p>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={jobsChartData}
                  margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                >
                  <CartesianGrid vertical={false} stroke="#f0f0f0" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 12, fill: "#9ca3af" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: "#9ca3af" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip content={<JobsTooltip />} />
                  <Bar
                    dataKey="success"
                    stackId="jobs"
                    fill="#10b981"
                    radius={[0, 0, 4, 4]}
                    barSize={64}
                    name="Success"
                  />
                  <Bar
                    dataKey="failure"
                    stackId="jobs"
                    fill="#f43f5e"
                    radius={[4, 4, 0, 0]}
                    barSize={64}
                    name="Failure"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>
        );
      })()}

      {costAnalysisData && !jobsSummaryData && (
        <section>
          <div className="flex items-center justify-between mb-[12px] flex-wrap gap-[8px]">
            <div>
              <h2 className="text-[22px] font-bold text-gray-900">
                Jobs Run Summary — {productName}
              </h2>
              <p className="text-[13px] text-gray-400 mt-[2px]">
                Job success &amp; failure counts with DBU consumption
              </p>
            </div>
          </div>
          <div className="flex items-center gap-[10px] bg-blue-50 border border-blue-100 rounded-[10px] px-[16px] py-[12px]">
            <DollarSign className="w-[16px] h-[16px] text-blue-400 flex-shrink-0" />
            <p className="text-[13px] text-blue-700">
              Job run data is not yet available for this product.
            </p>
          </div>
        </section>
      )}

      {!costAnalysisData && jobsSummaryData && (
        <section>
          <div className="mb-[12px]">
            <h2 className="text-[22px] font-bold text-gray-900">
              Cost Analysis — {productName}
            </h2>
            <p className="text-[13px] text-gray-400 mt-[2px]">
              {PIPELINE_TYPE}
            </p>
          </div>
          <div className="flex items-center gap-[10px] bg-blue-50 border border-blue-100 rounded-[10px] px-[16px] py-[12px]">
            <DollarSign className="w-[16px] h-[16px] text-blue-400 flex-shrink-0" />
            <p className="text-[13px] text-blue-700">
              Cost analysis data is not yet available for this product.
            </p>
          </div>
        </section>
      )}

    </div>
  );
};

export default CostTab;